import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import { useState, useEffect, useCallback, CSSProperties } from "react";
import ArrowLeft from "@/components/icons/arrow_left";
import ArrowRight from "@/components/icons/arrow_right";

import imgBiochar from "@/img/marketplace/biochar.jpg";
import imgBiooil from "@/img/marketplace/biooil.jpg";
import imgConcrete from "@/img/marketplace/concrete.jpg";
import imgDac from "@/img/marketplace/dac.jpg";
import imgKelp from "@/img/marketplace/kelp.jpg";
import imgOcean from "@/img/marketplace/ocean.jpg";
import imgOlivine from "@/img/marketplace/olivine.jpg";

const images = [
  {
    src: imgKelp,
    alt: "Kelp farming carbon removal",
  },
  {
    src: imgDac,
    alt: "Direct air carbon capture",
  },
  {
    src: imgBiooil,
    alt: "Bio-oil sequestration",
  },
  {
    src: imgOlivine,
    alt: "Olivine weathering carbon capture",
  },
  {
    src: imgOcean,
    alt: "Ocean mineralization sequestration",
  },
  {
    src: imgBiochar,
    alt: "Biochar carbon removal",
  },
  {
    src: imgConcrete,
    alt: "Mineralization in concrete",
  },
] as const;

const MarketplaceGallery = ({ style }: { style?: CSSProperties }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [pause, setPause] = useState(false);

  const [ref, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    defaultAnimation: { duration: 1000 },
    slides: { perView: 1 },
    created: () => setIsMounted(true),
    dragStarted: () => {
      setPause(true);
    },
    dragEnded: () => {
      setPause(false);
    },
  });

  const onPrev = useCallback(() => slider.current?.prev(), [slider]);
  const onNext = useCallback(() => slider.current?.next(), [slider]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!pause && slider) {
        onNext();
      }
    }, 4000);
    return () => {
      clearInterval(timer);
    };
  }, [pause, slider, onNext]);

  return (
    <section
      className="gallery"
      onMouseOver={() => setPause(true)}
      onMouseOut={() => setPause(false)}
      style={{
        opacity: isMounted ? 1 : 0,
        width: "min(100vw - 24px, 520px)",
        ...style,
      }}
    >
      <div ref={ref} className="keen-slider">
        {images.map((img) => (
          <figure key={img.alt} className="keen-slider__slide">
            <Image
              src={img.src}
              alt={img.alt}
              placeholder="blur"
              objectFit="cover"
            />
            <figcaption className="caption">{img.alt}</figcaption>
          </figure>
        ))}
      </div>
      <button onClick={onPrev} aria-label="Previous Image">
        <ArrowLeft aria-hidden />
      </button>
      <button onClick={onNext} aria-label="Next Image">
        <ArrowRight aria-hidden />
      </button>
      <style jsx>{`
        .gallery {
          position: relative;
          height: 100%;
          max-width: 100%;
          border-radius: 16px;
          z-index: 1;
          overflow: hidden;
          line-height: 0;
        }

        .keen-slider {
          min-width: 0px;
          height: 100%;
          width: 100%;
          display: flex;
          align-items: stretch;
          border-radius: 16px;
          user-select: none;
          touch-action: pan-y;
          transition: opacity 0.125s ease-in-out;
        }
        .keen-slider__slide {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          flex-shrink: 0;
          max-height: 100%;
          border-radius: 16px;
        }

        .keen-slider__slide > :global(span) {
          flex: 1 1 auto;
        }

        .caption {
          background-color: var(--module-color-hover);
          width: 100%;
          padding: 16px 32px;
          text-align: center;
          font-weight: bold;
          bottom: 0;
          left: 0;
          right: 0;
          margin: 0;
          line-height: 1;
          border-radius: 0 0 16px 16px;
        }

        button {
          -webkit-appearance: none;
          border: 0;
          background-color: var(--module-color-hover-quote);
          border-radius: 99999px;
          cursor: pointer;
          padding: 10px;
          width: 32px;
          height: 32px;
          bottom: 8px;
          position: absolute;
        }
        button:first-of-type {
          left: 8px;
        }
        button:last-of-type {
          right: 8px;
        }
      `}</style>
    </section>
  );
};

export default MarketplaceGallery;
